/**
 * requestAccess — RPC contract for the callable the landing's request form
 * calls to ask for an organization on Revent.
 *
 * Cross-process contract between the landing's request form (caller) and
 * Revia's server `requestAccess` handler (onCall). Both ends parse with
 * `.strict()` — an extra key fails the whole request. The caller needs no
 * sign-in.
 *
 * The server keys the request by `platformHmac(email)`
 * (`access-request.entity.ts`) and acts on what that key already holds. The
 * outcome acts only through mail; the caller never learns it:
 *
 * | the address holds | the server writes                                          | mail                     |
 * |-------------------|------------------------------------------------------------|--------------------------|
 * | nothing           | a `pending` request, `expiresAt` 6 months out              | "received"               |
 * | `pending`         | nothing                                                    | none                     |
 * | `later`           | reopened to `pending` with the form's fields, reviewer stamps cleared, new clock | "received" again |
 * | `granted`         | `grantMailedAt`, when it mails                             | the grant mail again     |
 *
 * A reopen takes the form's businessType, organization, phone and locale, so
 * a requester can correct them; `createdAt` stands. Mail is queued in the
 * same transaction and sent by a trigger, so every case answers after one
 * transaction and no case is slower than another. The grant mail to one
 * address goes out at most once per
 * `GRANT_MAIL_INTERVAL_HOURS` (24): when the request's `grantMailedAt` is
 * within that window the server sends nothing and writes nothing, and
 * answers alike. Without that bound an unauthenticated form would mail an
 * inbox as often as anyone submits it.
 *
 * Fields justified:
 *   businessType — the kind of events the organization runs.
 *   organization — the organization's name, as a founded organization's name is
 *                  validated.
 *   phone        — a number staff can call.
 *   email        — the address that will found the organization; trimmed
 *                  and lower-cased here, since the hash is over one
 *                  spelling.
 *   locale       — the language the landing is showing; the mails follow
 *                  it.
 *
 * Output: empty, in every case above, so the form cannot be used to learn
 * whether an address has asked, been set aside or been admitted. The form
 * confirms receipt on any success.
 *
 * Excluded:
 *   requestId / status — would answer exactly the probe the empty output
 *                        refuses.
 *
 * Errors (thrown HttpsError, not part of the Output schema):
 *   invalid-argument — the input does not parse.
 */
import { z } from "zod";
import {
	AccessRequestEmailSchema,
	AccessRequestLocaleSchema,
	AccessRequestOrganizationSchema,
	AccessRequestPhoneSchema,
	BusinessType,
} from "./access-request.entity.ts";

export const RequestAccessInput = z
	.object({
		businessType: BusinessType,
		organization: AccessRequestOrganizationSchema,
		phone: AccessRequestPhoneSchema,
		email: AccessRequestEmailSchema,
		locale: AccessRequestLocaleSchema,
	})
	.strict();
export type RequestAccessInput = z.infer<typeof RequestAccessInput>;

export const RequestAccessOutput = z.object({}).strict();
export type RequestAccessOutput = z.infer<typeof RequestAccessOutput>;
